import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import MarkdownCard from "@/components/app/MarkdownCard";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Search, Camera, TrendingDown, Sparkles, Tag, Award, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const REGIONS = ["All Aotearoa", "Waikato", "Auckland", "Bay of Plenty", "Wellington", "Canterbury", "Otago", "Northland"];

// Rough NZ region bounding boxes (lat_min, lat_max, lng_min, lng_max) — good enough for auto-select
const REGION_BOXES = [
  { name: "Northland",       lat: [-36.5, -34.3], lng: [172.5, 174.7] },
  { name: "Auckland",        lat: [-37.3, -36.4], lng: [174.4, 175.4] },
  { name: "Waikato",         lat: [-38.7, -36.9], lng: [174.5, 176.5] },
  { name: "Bay of Plenty",   lat: [-38.7, -37.0], lng: [175.7, 178.6] },
  { name: "Wellington",      lat: [-41.6, -40.4], lng: [174.5, 176.4] },
  { name: "Canterbury",      lat: [-45.0, -42.4], lng: [169.6, 173.1] },
  { name: "Otago",           lat: [-46.6, -44.4], lng: [168.0, 171.3] },
];

function regionFromCoords(lat, lng) {
  for (const r of REGION_BOXES) {
    if (lat >= r.lat[0] && lat <= r.lat[1] && lng >= r.lng[0] && lng <= r.lng[1]) return r.name;
  }
  return "All Aotearoa";
}

export default function Home() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [regionCounts, setRegionCounts] = useState([]);
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [region, setRegion] = useState(() => localStorage.getItem("bb_region") || "All Aotearoa");
  const [autoDetected, setAutoDetected] = useState(false);
  const [found, setFound] = useState(() => Number(localStorage.getItem("bb_found") || 0));
  const bumpFound = () => {
    const next = found + 1;
    setFound(next); localStorage.setItem("bb_found", String(next));
  };
  const changeRegion = (r) => {
    setRegion(r);
    setAutoDetected(false);
    localStorage.setItem("bb_region", r);
  };

  // Auto-detect region on first visit only
  useEffect(() => {
    if (localStorage.getItem("bb_region") || localStorage.getItem("bb_region_asked")) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        localStorage.setItem("bb_region_asked", "1");
        const guess = regionFromCoords(pos.coords.latitude, pos.coords.longitude);
        if (guess && guess !== "All Aotearoa") {
          setRegion(guess);
          setAutoDetected(true);
          localStorage.setItem("bb_region", guess);
        }
      },
      () => { localStorage.setItem("bb_region_asked", "1"); },
      { timeout: 6000, maximumAge: 24 * 3600 * 1000 }
    );
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (region && region !== "All Aotearoa") params.region = region;
      if (category) params.category = category;
      const { data } = await api.get("/markdowns", { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/categories"); setCats(data); } catch (e) { /* ignore */ }
      try { const { data } = await api.get("/regions/counts"); setRegionCounts(data); } catch (e) { /* ignore */ }
    })();
  }, []);

  useEffect(() => { load(); }, [region, category]);

  const totalSavings = items.reduce((sum, m) => sum + Math.max((m.was_price || 0) - (m.now_price || 0), 0), 0);

  return (
    <div>
      {/* Auto-detected banner */}
      {autoDetected && region !== "All Aotearoa" && (
        <div className="bg-emerald-50 border-b border-emerald-200 text-emerald-900" data-testid="auto-region-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 text-sm flex items-center gap-2 flex-wrap">
            <MapPin className="w-4 h-4" />
            Showing deals near you: <strong>{region}</strong>
            <button onClick={async ()=>{
              const email = window.prompt(`Get an email when a new markdown drops in ${region}?\n\nEnter your email:`);
              if (!email) return;
              try { await api.post("/region-follows", { email, region }); toast.success(`We'll email you on new ${region} deals`); }
              catch (e) { toast.error("Could not subscribe"); }
            }} className="underline underline-offset-4 text-xs font-semibold hover:text-emerald-950" data-testid="notify-nearby-btn">Notify me on new deals</button>
            <button onClick={()=>changeRegion("All Aotearoa")} className="ml-auto underline underline-offset-4 text-xs font-semibold hover:text-emerald-950" data-testid="clear-auto-region">Show all Aotearoa</button>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="hero-mesh border-b border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-5">
            <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-emerald-800 bg-emerald-100 px-3 py-1.5 rounded-full">
              <Sparkles className="w-3.5 h-3.5" />Live in-store markdowns · Aotearoa NZ
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-none text-slate-900 font-display" data-testid="hero-title">
              Kia ora. Save on <span className="text-emerald-800">real markdowns</span><br />
              from stores <span className="text-amber-500">right down the road</span>.
            </h1>
            <p className="text-base sm:text-lg text-slate-600 max-w-xl">
              Local shops snap photos of their in-store markdowns and post them live. Grab today&apos;s deals before they walk out the door.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/stores"><Button className="bg-emerald-800 hover:bg-emerald-900 text-white h-11 px-5" data-testid="cta-browse-stores">Browse stores</Button></Link>
              <Link to="/register"><Button variant="outline" className="h-11 px-5 border-emerald-300 text-emerald-900 hover:bg-emerald-50" data-testid="cta-list-store">
                <Camera className="w-4 h-4 mr-1.5" />Snap & list yours
              </Button></Link>
            </div>
            <div className="flex gap-6 pt-4 text-sm">
              <div><div className="font-display font-black text-2xl text-emerald-900">{items.length}</div><div className="text-slate-500 text-xs uppercase tracking-wider">Live deals</div></div>
              <div><div className="font-display font-black text-2xl text-amber-500">${totalSavings.toFixed(0)}</div><div className="text-slate-500 text-xs uppercase tracking-wider">Total savings today</div></div>
              <div data-testid="loyalty-badge"><div className="font-display font-black text-2xl text-emerald-800 flex items-center gap-1"><Award className="w-6 h-6" />{found}</div><div className="text-slate-500 text-xs uppercase tracking-wider">Your deals found{found >= 10 ? " · Deal Hunter 🏅" : ""}</div></div>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="absolute -top-4 -left-4 w-28 h-28 dot-grid opacity-60"></div>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
              <img src="/hero-nz-store.jpg" alt="Kiwi store with fresh produce markdowns" data-testid="home-hero-image" className="w-full h-[380px] object-cover" />
              <div className="absolute bottom-4 left-4 discount-pulse bg-red-600 text-white font-black text-sm px-3 py-1.5 rounded-full">
                <TrendingDown className="w-4 h-4 inline -mt-0.5 mr-1" />Snap. Post. Save.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Regions strip */}
      {regionCounts.length > 0 && (
        <section className="border-b border-slate-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-2 mb-2 text-xs font-black uppercase tracking-widest text-slate-500"><MapPin className="w-3.5 h-3.5" />Deals by region</div>
            <div className="flex flex-wrap gap-2" data-testid="regions-strip">
              {regionCounts.map(r => {
                const max = Math.max(...regionCounts.map(x => x.count));
                const pct = Math.max(20, Math.round((r.count / max) * 100));
                const active = region === r.region;
                return (
                  <button key={r.region} onClick={()=>changeRegion(active ? "All Aotearoa" : r.region)} className={`relative overflow-hidden rounded-xl border px-3 py-2 text-left min-w-[130px] ${active ? "border-emerald-800 ring-2 ring-emerald-200" : "border-slate-200 hover:border-emerald-300"}`} data-testid={`region-tile-${r.region}`}>
                    <div className="absolute inset-y-0 left-0 bg-emerald-100/60" style={{ width: `${pct}%` }} />
                    <div className="relative">
                      <div className="text-xs font-bold text-slate-900">{r.region}</div>
                      <div className="text-[11px] text-slate-500">{r.count} live</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Filters */}
      <section className="border-b border-slate-200 bg-white/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e)=>e.key==='Enter' && load()} placeholder="Search markdowns (e.g. sourdough, milk, beer)" className="pl-9 h-11" data-testid="search-input" />
          </div>
          <Select value={region} onValueChange={changeRegion}>
            <SelectTrigger className="w-full sm:w-56 h-11" data-testid="region-select"><SelectValue placeholder="Region" /></SelectTrigger>
            <SelectContent>{REGIONS.map(r => <SelectItem key={r} value={r} data-testid={`region-option-${r}`}>{r}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={load} className="bg-emerald-800 hover:bg-emerald-900 h-11 px-5" data-testid="search-btn">Search</Button>
        </div>
        {cats.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4 flex flex-wrap gap-2" data-testid="category-chips">
            <button onClick={()=>setCategory("")} className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border ${category==="" ? "bg-emerald-800 text-white border-emerald-800" : "bg-white text-slate-700 border-slate-200 hover:border-emerald-300"}`} data-testid="cat-all">All</button>
            {cats.map(c => (
              <button key={c.name} onClick={()=>setCategory(category===c.name?"":c.name)} className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border flex items-center gap-1 ${category===c.name ? "bg-emerald-800 text-white border-emerald-800" : "bg-white text-slate-700 border-slate-200 hover:border-emerald-300"}`} data-testid={`cat-${c.name}`}>
                <Tag className="w-3 h-3" />{c.name} <span className="opacity-60">· {c.count}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between mb-6">
          <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-slate-900">Fresh markdowns</h2>
          <span className="text-sm text-slate-500">{items.length} live · updated now</span>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({length: 8}).map((_,i)=> <div key={i} className="aspect-[4/3] bg-slate-100 rounded-2xl animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-slate-300 rounded-2xl bg-white" data-testid="empty-state">
            <TrendingDown className="w-10 h-10 mx-auto text-emerald-800 mb-3" />
            <h3 className="font-display font-bold text-lg">No markdowns yet in this region</h3>
            <p className="text-slate-500 text-sm mt-1">Be the first — <Link to="/register" className="text-emerald-800 underline underline-offset-4">register your store</Link> and post a snap.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="markdown-grid" onClick={bumpFound}>
            {items.map(m => <MarkdownCard key={m.id} m={m} />)}
          </div>
        )}
      </section>
    </div>
  );
}
